import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Result, Steps } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../../helpers/apiClient";
import getBookingSiteUrl from "../../../../dashboard/components/bookingSiteUrl";
import { resolveBookingHours, resolveBranchTimeZone } from "../../../../../helpers/operationalHours";
import { useUser } from "../../../../../contexts/UserContext/UserContext";
import { useToast } from "../../../../../contexts/ToastContext";
import useSolaCardPayment from "../../../../../components/payment/useSolaCardPayment";
import {
    PAYMENT_METHOD_VALUES,
    PaymentMethodSelector,
    buildAdminPaymentOptions,
    getDefaultPaymentMethod,
} from "../../../lib/reservationPayment";
import confirmDuePaymentFlow from "../../../lib/confirmDuePaymentFlow";
import {
    finalizeReservationExtension,
    getExtensionDueAmount,
    quoteReservationExtension,
    resolveExtensionInvoiceContext,
} from "../../../lib/reservationExtension/extensionApi";
import ExtendDropoffPicker from "./ExtendDropoffPicker";
import ExtendInvoiceSummary from "./ExtendInvoiceSummary";

const STEP_DROPOFF = 0;
const STEP_INVOICE = 1;
const STEP_PAYMENT = 2;

const locationIdOf = (location) =>
    typeof location === "string" ? location : String(location?._id ?? location?.id ?? "");

/**
 * The pickup branch with its own timezone and operational hours. The reservation only carries a
 * reference (or a partial copy), so look the full record up the same way the booking site does.
 */
const usePickupLocation = (isOpen, pickupLocation) => {
    const [location, setLocation] = useState(typeof pickupLocation === "object" ? pickupLocation : null);

    useEffect(() => {
        const id = locationIdOf(pickupLocation);
        if (!isOpen || !id) return undefined;

        let cancelled = false;
        apiClient
            .get("/api/location/list?limit=200")
            .then(({ data }) => {
                const locations = Array.isArray(data?.data) ? data.data : [];
                const match = locations.find((loc) => String(loc?._id ?? loc?.id ?? "") === id);
                if (!cancelled && match) setLocation(match);
            })
            .catch(() => {
                // Fall back to whatever the reservation carries; the server re-checks hours anyway.
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, pickupLocation]);

    return location;
};

/**
 * Extends a reservation's drop-off: choose the new drop-off (by days or on the calendar, per the
 * company's `extendReservationByDay` preference) → review the re-priced invoice → collect what is
 * owed by cash, card or payment link. Mirrors the booking frontend's extend flow.
 */
const ExtendReservationModal = ({ isOpen, setIsOpen, resDetails, onExtended }) => {
    const { t } = useTranslation(["reservations", "reservationPayment"]);
    const { myCompanyData: companyData } = useUser();
    const toast = useToast();
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();

    const [step, setStep] = useState(STEP_DROPOFF);
    const [newDropoff, setNewDropoff] = useState("");
    const [hasConflict, setHasConflict] = useState(false);
    const [invoice, setInvoice] = useState(null);
    const [invoiceContext, setInvoiceContext] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [paymentSystem, setPaymentSystem] = useState(null);

    const extendByDay = companyData?.preferences?.extendReservationByDay === true;
    const pickupLocation = usePickupLocation(isOpen, resDetails?.pickupLocation);
    const branchTimeZone = useMemo(
        () => resolveBranchTimeZone(companyData, pickupLocation),
        [companyData, pickupLocation],
    );
    const bookingHours = useMemo(() => resolveBookingHours(companyData, pickupLocation), [companyData, pickupLocation]);
    const bufferMinutes = useMemo(() => {
        if (companyData?.preferences?.enableBufferTime !== true) return 0;
        const minutes = Number(companyData?.bufferTimeMinutes);
        return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
    }, [companyData]);

    const showCashPayment = companyData?.preferences?.showCashPayment === true;
    const paymentOptions = useMemo(
        () =>
            buildAdminPaymentOptions({
                defaultPaymentGateway: companyData?.defaultPaymentGateway || "stripe",
                showCashPayment,
                t,
            }),
        [companyData?.defaultPaymentGateway, showCashPayment, t],
    );

    const extensionDue = getExtensionDueAmount(invoice, resDetails);

    // Check if the reservation has ended (dropoff time passed + insurance completed)
    const isReservationEnded = useMemo(() => {
        const dropoff = resDetails?.exactDropoffTime || resDetails?.dropoffDate;
        if (!dropoff) return false;
        const isDropoffPassed = new Date(dropoff) < new Date();

        const insuranceObj =
            resDetails?.teqMobilityInsurance || resDetails?.bonzahInsurance || resDetails?.abiInsurance;
        const isInsuranceEnded = insuranceObj?.status === "completed";

        return isDropoffPassed && isInsuranceEnded;
    }, [resDetails]);
    const bookingSiteUrl = getBookingSiteUrl(companyData?.id);

    const resetState = useCallback(() => {
        setStep(STEP_DROPOFF);
        setNewDropoff("");
        setHasConflict(false);
        setInvoice(null);
        setInvoiceContext(null);
        setInvoiceLoading(false);
        setSubmitLoading(false);
        setPaymentSystem(null);
    }, []);

    const handleClose = () => {
        resetState();
        setIsOpen(false);
    };

    const reportError = (error) => {
        toast.error({
            message: t("extendReservation.errorMessage"),
            description: error?.response?.data?.message || error?.message,
        });
    };

    const generateInvoice = async () => {
        setInvoiceLoading(true);
        setInvoice(null);
        try {
            const context = await resolveExtensionInvoiceContext(resDetails);
            setInvoiceContext(context);
            setInvoice(await quoteReservationExtension(resDetails, context, newDropoff));
        } catch (error) {
            reportError(error);
        } finally {
            setInvoiceLoading(false);
        }
    };

    const finishExtension = async (updatedReservation) => {
        toast.success({ message: t("extendReservation.successMessage") });
        handleClose();
        await onExtended?.(updatedReservation);
    };

    /** Nothing owed, or cash in hand: the backend records it and extends in one call. */
    const extendNow = async (cashAmount = 0) => {
        const updated = await finalizeReservationExtension(resDetails.id, {
            dropoff: newDropoff,
            context: invoiceContext || (await resolveExtensionInvoiceContext(resDetails)),
            cashAmount,
        });
        await finishExtension(updated);
    };

    const handleNext = async () => {
        if (step === STEP_DROPOFF) {
            if (!newDropoff || hasConflict) return;
            setStep(STEP_INVOICE);
            await generateInvoice();
            return;
        }

        if (step === STEP_INVOICE) {
            if (extensionDue > 0) {
                setPaymentSystem((current) => current ?? getDefaultPaymentMethod(paymentOptions));
                setStep(STEP_PAYMENT);
                return;
            }
            try {
                setSubmitLoading(true);
                await extendNow();
            } catch (error) {
                reportError(error);
                setSubmitLoading(false);
            }
        }
    };

    const handlePayAndExtend = async () => {
        if (!paymentSystem) {
            toast.error({ message: t("extendReservation.selectPaymentMethod") });
            return;
        }

        try {
            setSubmitLoading(true);

            if (paymentSystem === PAYMENT_METHOD_VALUES.CASH) {
                await extendNow(extensionDue);
                return;
            }

            const result = await confirmDuePaymentFlow({
                reservation: resDetails,
                dueAmount: extensionDue,
                paymentSystem,
                companyData,
                toast,
                t,
                requestSolaCardPayment,
                extension: {
                    dropoffDate: newDropoff,
                    invoiceContext: invoiceContext || (await resolveExtensionInvoiceContext(resDetails)),
                },
            });

            if (result?.type === "cancelled") {
                setSubmitLoading(false);
                return;
            }
            if (result?.type === "redirect") return;
            if (result?.type === "link_sent") {
                // The customer pays later; the drop-off moves once the payment clears.
                handleClose();
                return;
            }
            // Sola charged the card and the backend already extended the reservation.
            await finishExtension();
        } catch (error) {
            reportError(error);
            setSubmitLoading(false);
        }
    };

    const stepItems = [
        { title: extendByDay ? t("extendReservation.stepDays") : t("extendReservation.stepDateTime") },
        { title: t("extendReservation.stepInvoice") },
        { title: t("extendReservation.stepPayment") },
    ];

    const renderPrimaryButton = () => {
        if (step === STEP_PAYMENT) {
            return (
                <Button
                    type="primary"
                    shape="round"
                    loading={submitLoading}
                    disabled={!paymentSystem || submitLoading}
                    onClick={handlePayAndExtend}>
                    {t("extendReservation.payAndExtend")}
                </Button>
            );
        }
        if (step === STEP_INVOICE) {
            return (
                <Button
                    type="primary"
                    shape="round"
                    loading={submitLoading}
                    disabled={invoiceLoading || !invoice || submitLoading}
                    onClick={handleNext}>
                    {extensionDue > 0 ? t("extendReservation.next") : t("extendReservation.extend")}
                </Button>
            );
        }
        return (
            <Button type="primary" shape="round" disabled={!newDropoff || hasConflict} onClick={handleNext}>
                {t("extendReservation.next")}
            </Button>
        );
    };

    return (
        <>
            <Modal
                open={isOpen}
                title={null}
                onCancel={handleClose}
                maskClosable={false}
                destroyOnClose
                footer={null}
                centered
                width={{ xs: "calc(100vw - 32px)", lg: 720 }}>
                <div className="flex min-w-0 flex-col">
                    <div className="mb-4 sm:mb-6">
                        <h2 className="mb-2 text-xl font-bold sm:text-2xl">{t("extendReservation.title")}</h2>
                        {isReservationEnded ? null : (
                            <p className="text-base text-gray-500">
                                {extendByDay
                                    ? t("extendReservation.descriptionDays")
                                    : t("extendReservation.description")}
                            </p>
                        )}
                    </div>

                    {isReservationEnded ? (
                        <Result
                            status="warning"
                            title={t("extendReservation.reservationEndedWarning")}
                            extra={[
                                bookingSiteUrl ? (
                                    <Button
                                        key="book"
                                        type="primary"
                                        shape="round"
                                        onClick={() => {
                                            handleClose();
                                            window.open(bookingSiteUrl, "_blank", "noopener,noreferrer");
                                        }}>
                                        {t("extendReservation.bookAgain")}
                                    </Button>
                                ) : null,
                                <Button key="close" shape="round" onClick={handleClose}>
                                    {t("extendReservation.close")}
                                </Button>,
                            ]}
                        />
                    ) : (
                        <>
                            <Steps size="small" current={step} items={stepItems} className="mb-6" />

                            <div className="max-h-[60vh] min-w-0 overflow-y-auto">
                                {step === STEP_DROPOFF && resDetails ? (
                                    <ExtendDropoffPicker
                                        resDetails={resDetails}
                                        value={newDropoff}
                                        onChange={setNewDropoff}
                                        onConflictChange={setHasConflict}
                                        extendByDay={extendByDay}
                                        branchTimeZone={branchTimeZone}
                                        bookingHours={bookingHours}
                                        bufferMinutes={bufferMinutes}
                                        t={t}
                                    />
                                ) : null}

                                {step === STEP_INVOICE ? (
                                    <ExtendInvoiceSummary
                                        invoice={invoice}
                                        isLoading={invoiceLoading}
                                        reservation={resDetails}
                                        newDropoffDate={newDropoff}
                                        timeZone={branchTimeZone}
                                        t={t}
                                    />
                                ) : null}

                                {step === STEP_PAYMENT ? (
                                    <div className="space-y-4">
                                        <div className="rounded-lg border border-[#EEF0F6] bg-[#F0F7FF] px-4 py-3">
                                            <p className="fontRegular text-sm text-[#667085]">
                                                {t("extendReservation.paymentDueLabel")}
                                            </p>
                                            <p className="fontSemi mt-1 text-2xl text-[#5D5DF6]">
                                                ${extensionDue.toFixed(2)}
                                            </p>
                                        </div>
                                        <PaymentMethodSelector
                                            compact
                                            title={t("extendReservation.paymentMethodTitle")}
                                            subtitle={t("extendReservation.paymentMethodSubtitle")}
                                            options={paymentOptions}
                                            value={paymentSystem}
                                            onChange={setPaymentSystem}
                                        />
                                    </div>
                                ) : null}
                            </div>

                            <div className="mt-4 flex items-center justify-end gap-2 border-t border-[#f0f0f0] pt-4">
                                {step === STEP_DROPOFF ? (
                                    <Button shape="round" onClick={handleClose}>
                                        {t("extendReservation.cancel")}
                                    </Button>
                                ) : (
                                    <Button
                                        shape="round"
                                        disabled={submitLoading}
                                        onClick={() => setStep((previous) => previous - 1)}>
                                        {t("extendReservation.back")}
                                    </Button>
                                )}
                                {renderPrimaryButton()}
                            </div>
                        </>
                    )}
                </div>
            </Modal>
            {solaCardModal}
        </>
    );
};

ExtendReservationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    resDetails: PropTypes.object,
    onExtended: PropTypes.func,
};

export default ExtendReservationModal;
